// src/services/enduser/enduserAuthService.ts - Fixed with Debug

import { API_BASE_URL } from '../../config/api';

// Helper function to create FormData
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });

  return formData;
};

// Helper function to handle API responses - WITH DEBUG
const handleApiResponse = async (response: Response): Promise<any> => {
  // console.log('Response status:', response.status);
  // console.log('Response ok:', response.ok);
  // console.log('Response headers:', Object.fromEntries(response.headers.entries()));

  // Check if response is ok
  if (!response.ok) {
    console.error('HTTP error! status:', response.status);

    // Try to get error text
    try {
      const errorText = await response.text();
      console.error('Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    } catch (e) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Try to parse JSON
  try {
    const text = await response.text();
    // console.log('Raw response text:', text);

    if (!text) {
      throw new Error('Empty response body');
    }

    const result = JSON.parse(text);
    // console.log('Parsed JSON result:', result);
    return result;

  } catch (error) {
    console.error('Failed to parse JSON:', error);
    throw new Error('Failed to parse response as JSON');
  }
};

// Cookie management functions - FIXED
const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  // Remove secure flag for localhost development
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; samesite=lax`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; samesite=lax`;
};

// Interfaces (unchanged)
interface RegisterData {
  name: string;
  email: string;
  phone_number: string;
  user_type: 'personal' | 'commercial';
  company_name?: string;
  terms_accepted?: string;
}

interface LoginData {
  email: string;
  terms_accepted?: string;
}

interface VerifyEmailData {
  session_token: string;
  verification_code: string;
}

interface UserProfile {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  user_type: 'personal' | 'commercial';
  company_name?: string;
  verified: boolean;
  free_access: boolean;
  last_login: string;
}

interface SubscriptionStatus {
  status: 'active' | 'trial' | 'expired';
  type: string;
  can_calculate: boolean;
  can_view_history: boolean;
  expires_at?: string;
  trial_expires_at?: string;
  message: string;
  // ===== ניסיון חינם בעזרת קוד אימות (מאי 2026) =====
  free_trial_activated?: boolean;
  free_trial_used?: number;
  free_trial_remaining?: number;
}

interface SessionVerificationResponse {
  user_info: UserProfile;
  session_info: {
    expires_at: string;
    verified: boolean;
  };
  subscription_status: SubscriptionStatus;
}

interface ApiResponse<T = any> {
  res: 'OK' | 'NOK';
  msg: string;
  data?: T;
  error_code?: string;
}

interface PhoneCountryInfo {
  code: string;
  name: string;
}

class EnduserAuthService {
  private readonly SESSION_COOKIE_NAME = 'enduser_session_token';

  /**
   * Register new end user - WITH DEBUG
   */
  async register(data: RegisterData): Promise<ApiResponse> {
    // console.log('=== REGISTRATION START ===');
    // console.log('Registration data:', data);

    try {
      const formData = createFormData(data);

      // Debug FormData
      // console.log('FormData entries:');
      Array.from(formData.entries()).forEach(([key, value]) => {
        // console.log(`${key}:`, value);
      });

      // console.log('Making fetch request to:', `${API_BASE_URL}/Api/service/enduser/register`);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/register`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      // console.log('Raw fetch response:', response);

      const result = await handleApiResponse(response);
      // console.log('Final registration result:', result);

      // If successful, save session token
      if (result.res === 'OK' && result.data?.session_token) {
        // console.log('Saving session token:', result.data.session_token);
        this.setSessionToken(result.data.session_token);
      }

      // console.log('=== REGISTRATION SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== REGISTRATION ERROR ===');
      console.error('Registration error:', error);
      console.error('Error stack:', error instanceof Error ? error.stack : 'Unknown error');

      return {
        res: 'NOK',
        msg: 'שגיאה ברישום - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Login existing user - WITH DEBUG
   */
  async login(data: LoginData): Promise<ApiResponse> {
    // console.log('=== LOGIN START ===');
    // console.log('Login data:', data);

    try {
      const formData = createFormData(data);

      // console.log('Making fetch request to:', `${API_BASE_URL}/Api/service/enduser/login`);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/login`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);

      // If successful, save session token
      if (result.res === 'OK' && result.data?.session_token) {
        this.setSessionToken(result.data.session_token);
      }

      // console.log('=== LOGIN SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== LOGIN ERROR ===');
      console.error('Login error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה בהתחברות - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Verify email with code - WITH DEBUG
   */
  async verifyEmail(data: VerifyEmailData): Promise<ApiResponse> {
    // console.log('=== EMAIL VERIFICATION START ===');
    // console.log('Verification data:', data);

    try {
      const formData = createFormData(data);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/verify_email`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);

      // Update session token if provided
      if (result.res === 'OK' && result.data?.session_token) {
        this.setSessionToken(result.data.session_token);
      }

      // console.log('=== EMAIL VERIFICATION SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== EMAIL VERIFICATION ERROR ===');
      console.error('Email verification error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה באימות האימייל - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }

  /**
   * Verify current session and get user data
   */
  async verifySession(): Promise<ApiResponse<SessionVerificationResponse>> {
    const sessionToken = this.getSessionToken();

    if (!sessionToken) {
      return {
        res: 'NOK',
        msg: 'לא נמצא טוקן סשן',
        error_code: 'NO_SESSION_TOKEN'
      };
    }

    try {
      const formData = createFormData({ session_token: sessionToken });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/verify_session`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);

      if (result.res === 'NOK') {
        // If session is invalid, remove it
        this.logout();
      }

      return result;

    } catch (error) {
      console.error('Session verification error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה באימות הסשן',
        error_code: 'VERIFICATION_ERROR'
      };
    }
  }

  /**
   * Get user profile (requires valid session)
   */
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    const sessionToken = this.getSessionToken();

    // console.log('🔍 getProfile - sessionToken:', sessionToken ? 'EXISTS' : 'NULL');
    // console.log('🔍 getProfile - token length:', sessionToken?.length);

    if (!sessionToken) {
      // console.log('❌ No session token found in cookies');
      return {
        res: 'NOK',
        msg: 'לא נמצא טוקן סשן',
        error_code: 'NO_SESSION_TOKEN'
      };
    }

    try {
      const formData = new FormData();

      const headers = {
        'Authorization': `Bearer ${sessionToken}`
      };

      // console.log('🔍 getProfile - About to fetch with headers:', headers);
      // console.log('🔍 getProfile - API_BASE_URL:', API_BASE_URL);

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_profile`, {
        method: 'POST',
        headers: headers,
        body: formData,
        credentials: 'include',
      });

      // console.log('✅ getProfile - Response received:', response.status);
      const result = await handleApiResponse(response);
      return result;

    } catch (error) {
      console.error('Get profile error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה בקבלת פרטי המשתמש',
        error_code: 'PROFILE_ERROR'
      };
    }
  }
  /**
     * Delete user account - GDPR/Israeli Privacy Law compliance
     */
  async deleteAccount(confirmationText: string): Promise<ApiResponse> {
    // console.log('=== ACCOUNT DELETION START ===');
    // console.log('Confirmation text provided:', confirmationText);

    const sessionToken = this.getSessionToken();

    if (!sessionToken) {
      // console.log('❌ No session token found');
      return {
        res: 'NOK',
        msg: 'לא נמצא טוקן סשן - נא להתחבר מחדש',
        error_code: 'NO_SESSION_TOKEN'
      };
    }

    if (confirmationText !== 'DELETE') {
      // console.log('❌ Invalid confirmation text');
      return {
        res: 'NOK',
        msg: 'יש להקליד DELETE לאישור המחיקה',
        error_code: 'INVALID_CONFIRMATION'
      };
    }

    try {
      const formData = createFormData({ confirmation_text: confirmationText });

      // console.log('Making fetch request to delete account');

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/delete_account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        body: formData,
        credentials: 'include',
      });

      const result = await handleApiResponse(response);

      // If successful, immediately logout and clear session
      if (result.res === 'OK') {
        // console.log('✅ Account deleted successfully, clearing session');
        this.logout();
      }

      // console.log('=== ACCOUNT DELETION SUCCESS ===');
      return result;

    } catch (error) {
      console.error('=== ACCOUNT DELETION ERROR ===');
      console.error('Account deletion error:', error);
      return {
        res: 'NOK',
        msg: 'שגיאה במחיקת החשבון - ' + (error instanceof Error ? error.message : 'Unknown error'),
        error_code: 'NETWORK_ERROR'
      };
    }
  }
  /**
   * Logout user
   */
  logout(): void {
    this.deleteSessionToken();
  }

  /**
   * Session token management
   */
  setSessionToken(token: string): void {
    setCookie(this.SESSION_COOKIE_NAME, token, 365 * 10); // 10 years
  }

  getSessionToken(): string | null {
    return getCookie(this.SESSION_COOKIE_NAME);
  }

  deleteSessionToken(): void {
    deleteCookie(this.SESSION_COOKIE_NAME);
  }

  /**
   * Check if user is authenticated (has valid session token)
   */
  isAuthenticated(): boolean {
    return !!this.getSessionToken();
  }

  /**
   * Subscription helper functions
   */
  canAccessCalculators(subscriptionStatus: SubscriptionStatus): boolean {
    return subscriptionStatus.can_calculate;
  }

  canViewHistory(subscriptionStatus: SubscriptionStatus): boolean {
    return subscriptionStatus.can_view_history;
  }

  getSubscriptionMessage(subscriptionStatus: SubscriptionStatus): string {
    return subscriptionStatus.message;
  }

  /**
   * Check if subscription allows calculator access
   */
  isCalculatorAccessAllowed(subscriptionStatus?: SubscriptionStatus): boolean {
    if (!subscriptionStatus) return false;
    return this.canAccessCalculators(subscriptionStatus);
  }

  /**
   * Check if subscription allows history access
   */
  isHistoryAccessAllowed(subscriptionStatus?: SubscriptionStatus): boolean {
    if (!subscriptionStatus) return true; // Always allow history viewing
    return this.canViewHistory(subscriptionStatus);
  }

  /**
   * Get subscription status message for UI
   */
  getSubscriptionStatusMessage(subscriptionStatus?: SubscriptionStatus): string {
    if (!subscriptionStatus) return 'לא נמצא מידע על מנוי';
    return this.getSubscriptionMessage(subscriptionStatus);
  }

  /**
   * Check if user has free access
   */
  hasFreeAccess(userProfile?: UserProfile): boolean {
    return userProfile?.free_access || false;
  }

  /**
   * Get user display name
   */
  getUserDisplayName(userProfile?: UserProfile): string {
    if (!userProfile) return 'משתמש';

    if (userProfile.user_type === 'commercial' && userProfile.company_name) {
      return `${userProfile.name} (${userProfile.company_name})`;
    }

    return userProfile.name;
  }

  /**
   * Format user type for display
   */
  formatUserType(userType: 'personal' | 'commercial'): string {
    return userType === 'personal' ? 'אישי' : 'עסקי';
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate Israeli phone number (legacy support)
   */
  isValidIsraeliPhone(phone: string): boolean {
    const phoneRegex = /^0(5[^7]|[2-4]|[8-9]|7[0-9])[0-9]{7}$/;
    return phoneRegex.test(phone);
  }

  /**
   * Validate international phone number (NEW)
   */
  isValidInternationalPhone(phone: string): boolean {
    // Remove all non-digit characters except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');

    // Must start with +
    if (!cleanPhone.startsWith('+')) {
      return false;
    }

    // Remove the + for validation
    const phoneDigits = cleanPhone.substring(1);

    // Must have 7-15 digits (international standard)
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      return false;
    }

    // Must contain only digits after the +
    if (!/^\d+$/.test(phoneDigits)) {
      return false;
    }

    return true;
  }

  /**
   * Format international phone number for display (NEW)
   */
  formatInternationalPhoneNumber(phone: string): string {
    if (!phone) return '';

    // נרמול לפורמט קנוני. מספר ישראלי → +972XXXXXXXXX (ללא 0 מוביל מיותר).
    const digits = String(phone).replace(/\D/g, '');
    if (!digits) return '';

    // קידומת ישראל קיימת (כולל הווריאנט השגוי 9720XXXXXXXXX)
    if (digits.startsWith('972')) {
      let rest = digits.slice(3);
      if (rest.startsWith('0')) rest = rest.slice(1);
      return '+972' + rest;
    }

    // מספר מקומי ישראלי עם 0 מוביל (0XXXXXXXXX) → +972XXXXXXXXX
    if (digits.startsWith('0')) {
      return '+972' + digits.slice(1);
    }

    // אחרת — מספר בינלאומי אמיתי, רק מוסיפים +
    return '+' + digits;
  }

  /**
   * Get country name from phone number (NEW)
   */
  getPhoneCountryInfo(phone: string): PhoneCountryInfo {
    if (!phone.startsWith('+')) return { code: '', name: '' };

    const countryMappings: { [key: string]: string } = {
      '+1': 'ארה"ב/קנדה',
      '+44': 'בריטניה',
      '+33': 'צרפת',
      '+49': 'גרמניה',
      '+39': 'איטליה',
      '+34': 'ספרד',
      '+7': 'רוסיה',
      '+86': 'סין',
      '+81': 'יפן',
      '+91': 'הודו',
      '+61': 'אוסטרליה',
      '+55': 'ברזיל',
      '+52': 'מקסיקו',
      '+971': 'איחוד האמירויות',
      '+966': 'סעודיה',
      '+20': 'מצרים',
      '+27': 'דרום אפריקה',
      '+90': 'טורקיה',
      '+30': 'יוון',
      '+31': 'הולנד',
      '+32': 'בלגיה',
      '+41': 'שוויץ',
      '+43': 'אוסטריה',
      '+45': 'דנמרק',
      '+46': 'שוודיה',
      '+47': 'נורווגיה',
      '+48': 'פולין',
      '+351': 'פורטוגל',
      '+353': 'אירלנד',
      '+358': 'פינלנד',
      '+36': 'הונגריה',
      '+420': 'צ\'כיה',
      '+421': 'סלובקיה',
      '+385': 'קרואטיה',
      '+386': 'סלובניה',
      '+387': 'בוסניה',
      '+389': 'מקדוניה',
      '+381': 'סרביה',
      '+382': 'מונטנגרו',
      '+383': 'קוסובו',
      '+972': 'ישראל',
      '+962': 'ירדן',
      '+961': 'לבנון',
      '+963': 'סוריה',
      '+964': 'עיראק',
      '+965': 'כווית',
      '+967': 'תימן',
      '+968': 'עומאן',
      '+973': 'בחריין',
      '+974': 'קטר',
      '+975': 'בהוטן',
      '+976': 'מונגוליה',
      '+977': 'נפאל',
      '+992': 'טג\'יקיסטן',
      '+993': 'טורקמניסטן',
      '+994': 'אזרבייג\'ן',
      '+995': 'גאורגיה',
      '+996': 'קירגיזסטן',
      '+998': 'אוזבקיסטן'
    };

    // Try to find the longest matching prefix
    for (let i = 4; i >= 2; i--) {
      const prefix = phone.substring(0, i);
      if (countryMappings[prefix]) {
        return { code: prefix, name: countryMappings[prefix] };
      }
    }

    return { code: '', name: 'לא זוהה' };
  }

  /**
   * Format phone number for display (legacy support)
   */
  formatPhoneNumber(phone: string): string {
    if (phone.startsWith('+')) {
      // International format - show as is
      return phone;
    }

    if (phone.length === 10) {
      // Israeli format
      return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
    }
    return phone;
  }
}

// Export singleton instance
export const enduserAuthService = new EnduserAuthService();
export default enduserAuthService;